// # Mentor Meetings Component
import React, { useState, useEffect } from 'react';
import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  serverTimestamp,
  getDocs,
  query,
  where 
} from 'firebase/firestore';
import { db, functions } from '../../../../firebase/config';
import { httpsCallable } from 'firebase/functions';
import { useAuthStore } from '../../../../auth/state';
import './mentor_meetings.css';
import DashboardHeader from '../../../../components/DashboardHeader';
import DashboardFooter from '../../../../components/DashboardFooter';
import { FaCalendarAlt, FaClock, FaExternalLinkAlt, FaPlus, FaCheck, FaTimes, FaUser, FaUsers, FaEdit, FaTrash } from 'react-icons/fa';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker, TimePicker } from '@mui/x-date-pickers';
import { TextField, CircularProgress } from '@mui/material';
import MeetingActions, { EditMeetingDialog, DeleteMeetingDialog } from './mentor_meet_tools';
import cacheManager from '../../../../auth/cache_manager';

// # MentorMeetingModal component for creating meetings
export const MentorMeetingModal = ({ open, onClose, meetingType, onSuccess, mentorUrl, mentorEmail, isDarkMode }) => {
  const { user, userProfile } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mentees, setMentees] = useState([]);
  const [loadingMentees, setLoadingMentees] = useState(false);
  const [selectedMentees, setSelectedMentees] = useState([]);
  const [showMenteeSelector, setShowMenteeSelector] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // # Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    selectedDate: null,
    selectedTime: null,
    meetingUrl: ''
  });

  // # Check if we have preselected mentee from mentees list
  const [preSelectedMenteeId, setPreSelectedMenteeId] = useState(
    sessionStorage.getItem('scheduleMeetingMenteeId')
  );

  // # Initialize meeting URL from mentor profile
  useEffect(() => {
    if (open) {
      setFormData(prev => ({
        ...prev,
        meetingUrl: mentorUrl || ''
      }));
      
      // Only fetch mentees if we have a mentor email
      if (mentorEmail) {
        console.log(`Initializing meeting modal with mentor email: ${mentorEmail}`);
        fetchMentees();
      } else {
        console.warn('No mentor email provided - cannot fetch mentees');
        setMentees([]);
      }
    }
  }, [open, mentorUrl, mentorEmail]);

  // # Effect to pre-select mentee if specified
  useEffect(() => {
    if (preSelectedMenteeId && mentees.length > 0) {
      // # Find the mentee in our list
      const menteeToSelect = mentees.find(m => m.uid === preSelectedMenteeId);
      if (menteeToSelect) {
        setSelectedMentees([menteeToSelect]);
        // # Clear the preselected ID after using it
        setPreSelectedMenteeId(null);
      }
    }
  }, [preSelectedMenteeId, mentees]);

  // # Reset form when modal closes
  useEffect(() => {
    if (!open) {
      setFormData({
        title: '',
        description: '',
        selectedDate: null,
        selectedTime: null,
        meetingUrl: mentorUrl || ''
      });
      setSelectedMentees([]);
      setError('');
    }
  }, [open, mentorUrl]);

  // # Fetch mentees based on meeting type
  const fetchMentees = async () => {
    if (!user?.uid || !mentorEmail) return;
    
    setLoadingMentees(true);
    try {
      // # Determine which subscription type to query based on meeting type
      const subscriptionType = meetingType === 'step1' 
        ? 'step1_mentorship' 
        : meetingType === 'step2' 
          ? 'step2_mentorship' 
          : null;
      
      if (!subscriptionType) {
        throw new Error('Invalid meeting type');
      }
      
      console.log(`Fetching mentees for subscription type: ${subscriptionType}, mentor email: ${mentorEmail}`);
      
      // # Use the indexed query as specified in firestore.indexes.json
      const subscriptionsRef = collection(db, 'subscriptions');
      const subscriptionsQuery = query(
        subscriptionsRef,
        where(`subscriptions.${subscriptionType}.mentor.email`, '==', mentorEmail),
        where(`subscriptions.${subscriptionType}.status`, '==', 'active')
      );
      
      const subscriptionsSnapshot = await getDocs(subscriptionsQuery);
      
      if (subscriptionsSnapshot.empty) {
        console.log(`No active mentees found for ${subscriptionType}`);
        setMentees([]);
        setLoadingMentees(false);
        return;
      }
      
      console.log(`Found ${subscriptionsSnapshot.size} mentees`);
      
      // # Collect user IDs to fetch in batch
      const userIds = subscriptionsSnapshot.docs.map(doc => doc.id);
      
      // # Batch get user documents
      const usersRef = collection(db, 'users');
      
      // # Process users in batches of 10 to avoid large reads
      const batchSize = 10;
      const menteesList = [];
      
      for (let i = 0; i < userIds.length; i += batchSize) {
        const batchIds = userIds.slice(i, i + batchSize);
        const userPromises = batchIds.map(userId => 
          getDoc(doc(db, 'users', userId))
        );
        
        const userDocs = await Promise.all(userPromises);
        
        userDocs.forEach(userDoc => {
          if (userDoc.exists()) {
            const userData = userDoc.data();
            menteesList.push({
              uid: userDoc.id,
              name: `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
              email: userData.email,
              username: userData.username,
              profileImage: userData.profileImage || userData.photoURL || null
            });
          }
        });
      }
      
      // # Sort mentees by name for better UX
      menteesList.sort((a, b) => a.name.localeCompare(b.name));
      
      setMentees(menteesList);
    } catch (err) {
      console.error('Error fetching mentees:', err);
      setError('Failed to load mentees. Please try again.');
    } finally {
      setLoadingMentees(false);
    }
  };

  // # Handle form changes
  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    setError('');
  };

  // # Handle date/time picker changes
  const handleDateChange = (newDate) => {
    setFormData(prev => ({
      ...prev,
      selectedDate: newDate
    }));
    setError('');
  };

  const handleTimeChange = (newTime) => {
    setFormData(prev => ({
      ...prev,
      selectedTime: newTime
    }));
    setError('');
  };

  // # Toggle mentee selection
  const handleMenteeSelection = (mentee) => {
    setSelectedMentees(prev => {
      // # Check if mentee is already selected
      const isSelected = prev.some(m => m.uid === mentee.uid);
      
      // # If selected, remove from selection; otherwise add to selection
      return isSelected 
        ? prev.filter(m => m.uid !== mentee.uid)
        : [...prev, mentee];
    });
  };

  // # Filter mentees based on search query
  const filteredMentees = mentees.filter(mentee => {
    if (!searchQuery.trim()) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      mentee.name.toLowerCase().includes(query) ||
      mentee.email.toLowerCase().includes(query) ||
      (mentee.username && mentee.username.toLowerCase().includes(query))
    );
  });

  // # Combine date and time for meeting
  const combineDateAndTime = () => {
    if (!formData.selectedDate || !formData.selectedTime) return null;
    
    const date = new Date(formData.selectedDate);
    const time = new Date(formData.selectedTime);
    
    return new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      time.getHours(),
      time.getMinutes()
    );
  };

  // # Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // # Validate form
      const { title, description, selectedDate, selectedTime, meetingUrl } = formData;
      
      if (!title?.trim()) throw new Error('Please enter a meeting title');
      if (!description?.trim()) throw new Error('Please enter a meeting description');
      if (!selectedDate) throw new Error('Please select a meeting date');
      if (!selectedTime) throw new Error('Please select a meeting time');
      if (!selectedMentees?.length) throw new Error('Please select at least one mentee');
      if (!meetingUrl?.trim()) throw new Error('Meeting URL is required');

      const meetTime = combineDateAndTime();
      if (!meetTime) throw new Error('Invalid date/time combination');

      // # Get callable reference to cloud function
      const createMeetingFn = httpsCallable(functions, 'createMeeting');
      
      // # Map meeting type to document type
      const docType = meetingType === 'step1' ? 'step1' : 'step2';
      
      // # Call the function with meeting data
      const result = await createMeetingFn({
        type: docType,
        title: title.trim(),
        description: description.trim(),
        meetTime: meetTime.toISOString(),
        invited: selectedMentees.map(mentee => mentee.uid),
        meetingUrl: meetingUrl.trim(),
        hostData: {
          uid: user.uid,
          displayName: `${userProfile.firstName || ''} ${userProfile.lastName || ''}`.trim(),
          email: userProfile.email
        }
      });

      // # Check result
      if (result.data && result.data.success) {
        console.log(`Meeting created with ID: ${result.data.meetingId}`);
        onSuccess && onSuccess(result.data.meetingId);
      } else {
        throw new Error(result.data?.message || 'Failed to create meeting: Unknown error');
      }
    } catch (err) {
      console.error('Error creating meeting:', err);
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  // # When modal is not open, return null
  if (!open) return null;

  return (
    <div className={`mtg_modal_overlay ${isDarkMode ? 'mntr_mtg_dark-mode' : ''}`} onClick={onClose}>
      <div className="mtg_modal_container" onClick={e => e.stopPropagation()}>
        <div className="mtg_modal_header">
          <h2>{meetingType === 'step1' ? 'Create Step 1 Meeting' : 'Create Step 2 Meeting'}</h2>
          <button className="mtg_modal_close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="mtg_modal_form">
          {/* Title input */}
          <div className="mtg_modal_field">
            <label htmlFor="meeting-title">Title</label>
            <input
              id="meeting-title"
              type="text"
              value={formData.title}
              onChange={handleInputChange('title')}
              placeholder="Enter meeting title"
              required
            />
          </div>

          {/* Meeting URL input */}
          <div className="mtg_modal_field">
            <label htmlFor="meeting-url">Meeting URL</label>
            <input
              id="meeting-url"
              type="url"
              value={formData.meetingUrl}
              onChange={handleInputChange('meetingUrl')}
              placeholder="https://zoom.us/j/..."
              required
            />
          </div>

          {/* Date and time selection */}
          <div className="mtg_modal_datetime">
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <div className="mtg_modal_field">
                <label htmlFor="meeting-date">Date</label>
                <DatePicker
                  value={formData.selectedDate}
                  onChange={handleDateChange}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: "outlined",
                      error: !!error && !formData.selectedDate,
                      helperText: !!error && !formData.selectedDate ? 'Date is required' : '',
                      size: "small"
                    }
                  }}
                />
              </div>
              <div className="mtg_modal_field">
                <label htmlFor="meeting-time">Time</label>
                <TimePicker
                  value={formData.selectedTime}
                  onChange={handleTimeChange}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: "outlined",
                      error: !!error && !formData.selectedTime,
                      helperText: !!error && !formData.selectedTime ? 'Time is required' : '',
                      size: "small"
                    }
                  }}
                />
              </div>
            </LocalizationProvider>
          </div>

          {/* Mentee selection */}
          <div className="mtg_modal_field">
            <label>
              Select Mentees 
              <span className="mtg_modal_selected_count">
                ({selectedMentees.length} selected)
              </span>
            </label>
            <button 
              type="button" 
              className="mtg_modal_mentee_selector_toggle"
              onClick={() => setShowMenteeSelector(!showMenteeSelector)}
            >
              <FaUsers />
              {showMenteeSelector ? 'Hide Mentees' : 'Show Mentees'}
            </button>

            {showMenteeSelector && (
              <div className="mtg_modal_mentee_selector">
                <div className="mtg_modal_mentee_search">
                  <input
                    type="text"
                    placeholder="Search mentees..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                
                <div className="mtg_modal_mentee_list">
                  {loadingMentees ? (
                    <div className="mtg_modal_loading">
                      <CircularProgress size={24} />
                      <span>Loading mentees...</span>
                    </div>
                  ) : filteredMentees.length > 0 ? (
                    filteredMentees.map(mentee => (
                      <div 
                        key={mentee.uid}
                        className={`mtg_modal_mentee_item ${
                          selectedMentees.some(m => m.uid === mentee.uid) ? 'selected' : ''
                        }`}
                        onClick={() => handleMenteeSelection(mentee)}
                      >
                        <div className="mtg_modal_mentee_avatar">
                          {mentee.profileImage ? (
                            <img 
                              src={mentee.profileImage} 
                              alt={mentee.name} 
                              className="mtg_modal_mentee_img" 
                            />
                          ) : (
                            <div className="mtg_modal_mentee_placeholder">
                              {mentee.name.charAt(0).toUpperCase()}
                            </div>
                          )}
                        </div>
                        <div className="mtg_modal_mentee_info">
                          <span className="mtg_modal_mentee_name">{mentee.name}</span>
                          <span className="mtg_modal_mentee_email">{mentee.email}</span>
                        </div>
                        <div className="mtg_modal_mentee_checkbox">
                          {selectedMentees.some(m => m.uid === mentee.uid) && (
                            <FaCheck />
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="mtg_modal_no_mentees">
                      {searchQuery ? 'No mentees match your search' : mentorEmail ? 'No active mentees found for this mentorship type' : 'No mentor email configured - please update your profile first'}
                    </div>
                  )}
                </div>
              </div>
            )}

            {selectedMentees.length > 0 && (
              <div className="mtg_modal_selected_mentees">
                {selectedMentees.map(mentee => (
                  <div key={mentee.uid} className="mtg_modal_selected_mentee">
                    <div className="mtg_modal_selected_mentee_avatar">
                      {mentee.profileImage ? (
                        <img 
                          src={mentee.profileImage} 
                          alt={mentee.name} 
                          className="mtg_modal_selected_mentee_img" 
                        />
                      ) : (
                        <div className="mtg_modal_selected_mentee_placeholder">
                          {mentee.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <span>{mentee.name}</span>
                    <button 
                      type="button" 
                      onClick={() => handleMenteeSelection(mentee)}
                      className="mtg_modal_remove_mentee"
                    >
                      <FaTimes />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Description textarea */}
          <div className="mtg_modal_field">
            <label htmlFor="meeting-description">Description</label>
            <textarea
              id="meeting-description"
              value={formData.description}
              onChange={handleInputChange('description')}
              placeholder="Enter meeting details"
              rows={4}
              required
            />
          </div>

          {/* Error message */}
          {error && (
            <div className="mtg_modal_error">
              <FaTimes />
              <span>{error}</span>
            </div>
          )}

          {/* Form actions */}
          <div className="mtg_modal_actions">
            <button 
              type="button" 
              className="mtg_modal_cancel"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="mtg_modal_submit"
              disabled={loading}
            >
              {loading ? (
                <>
                  <CircularProgress size={16} color="inherit" />
                  <span>Creating...</span>
                </>
              ) : (
                <span>Create Meeting</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const MentorMeetings = () => {
  const { user, userProfile } = useAuthStore();
  const [zoomId, setZoomId] = useState('');
  const [email, setEmail] = useState('');
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  // # Meeting modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedMeetingType, setSelectedMeetingType] = useState('');
  const [showTypeSelector, setShowTypeSelector] = useState(false);
  
  // # Edit and delete meeting state
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  const [newMeeting, setNewMeeting] = useState({
    title: '',
    date: '',
    time: '',
    duration: 60,
    description: ''
  });

  const [isDarkMode, setIsDarkMode] = useState(false);

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (user?.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, [user]);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (user?.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', user.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${user.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Group meetings into logical sections
  const groupMeetings = (meetings) => {
    const now = new Date();
    const within24Hours = [];
    const within7Days = [];
    const otherMeetings = [];
    const passedMeetings = [];

    meetings.forEach(meeting => {
      const meetingTime = new Date(meeting.meet_time);
      const hoursSinceMeeting = (now - meetingTime) / (1000 * 60 * 60);
      const hoursUntilMeeting = (meetingTime - now) / (1000 * 60 * 60);
      const daysUntilMeeting = hoursUntilMeeting / 24;
      
      // # Meeting happened over 6 hours ago, place in passed section
      if (hoursSinceMeeting > 6) {
        passedMeetings.push(meeting);
        return;
      }
      
      // # Within the 6-hour window after meeting or upcoming
      if (hoursUntilMeeting < 0) {
        // # Meeting already started but within joinable window (past start but less than 6 hours)
        within24Hours.push(meeting);
      } else if (hoursUntilMeeting <= 24) {
        // # Meeting is within next 24 hours
        within24Hours.push(meeting);
      } else if (daysUntilMeeting <= 7) {
        // # Meeting is within next 7 days
        within7Days.push(meeting);
      } else {
        // # Meeting is more than 7 days away
        otherMeetings.push(meeting);
      }
    });
    
    // # Sort each section by meeting time
    const sortByTime = (a, b) => new Date(a.meet_time) - new Date(b.meet_time);
    within24Hours.sort(sortByTime);
    within7Days.sort(sortByTime);
    otherMeetings.sort(sortByTime);
    passedMeetings.sort((a, b) => new Date(b.meet_time) - new Date(a.meet_time)); // # Reverse sort for passed meetings
    
    return {
      within24Hours,
      within7Days,
      otherMeetings,
      passedMeetings
    };
  };

  // # Get user role directly from token claims
  const getUserRole = async () => {
    try {
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      return userDoc.exists() ? userDoc.data().role : null;
    } catch (error) {
      console.error('Error getting user role:', error);
      return null;
    }
  };

  // # Fetch mentor data and all relevant meetings on component mount
  useEffect(() => {
    const fetchData = async () => {
      if (!user?.uid) return;

      try {
        // # Get user role
        const userRole = await getUserRole();
        const isTutor = userRole === 'tutor';

        // # Fetch mentor profile data
        const mentorRef = doc(db, 'mentors', user.uid);
        const mentorDoc = await getDoc(mentorRef);

        if (mentorDoc.exists() || isTutor) {
          // # For tutors who might not have a mentor doc, we'll still show calendar
          // # but without checkups until they set up their mentor profile
          if (mentorDoc.exists()) {
            const data = mentorDoc.data();
            setZoomId(data.personal?.zoom_url || '');
            setEmail(data.personal?.mentor_email || '');
          }

          // # Fetch all meeting types
          const meetingTypes = ['gen_meetings', 'step1_meetings', 'step2_meetings', 'oet_meetings', 'visa_meetings'];
          let allMeetings = [];

          for (const type of meetingTypes) {
            const meetingsRef = doc(db, 'meetings', type);
            const meetingsDoc = await getDoc(meetingsRef);

            if (meetingsDoc.exists()) {
              const data = meetingsDoc.data();
              // # Convert the meetings object to array and properly handle timestamps
              const typeMeetings = Object.entries(data).map(([id, meeting]) => ({
                id,
                type,
                ...meeting,
                // # Properly convert Firestore timestamp or string date to Date object
                meet_time: meeting.meet_time?.toDate?.() || new Date(meeting.meet_time)
              })).filter(meeting => 
                // # Filter meetings where user is either host or invited
                meeting.host.uid === user.uid || 
                meeting.invited.includes(user.uid)
              );
              allMeetings = [...allMeetings, ...typeMeetings];
            }
          }

          // # Sort meetings by date (newest first)
          allMeetings.sort((a, b) => a.meet_time - b.meet_time);
          setMeetings(allMeetings);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load meetings data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user?.uid]);

  // # Format date and time with proper timezone handling
  const formatDateTime = (date) => {
    if (!date || !(date instanceof Date)) {
      return { date: 'Invalid Date', time: 'Invalid Time' };
    }

    try {
      return {
        date: date.toLocaleDateString('en-US', {
          weekday: 'short',
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          timeZone: 'Africa/Cairo' // # Set to Egypt timezone
        }),
        time: date.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          timeZone: 'Africa/Cairo' // # Set to Egypt timezone
        })
      };
    } catch (error) {
      console.error('Date formatting error:', error);
      return { date: 'Invalid Date', time: 'Invalid Time' };
    }
  };

  // # Handle profile update
  const handleProfileUpdate = async () => {
    if (!user?.uid) return;

    try {
      // # Get user role
      const userRole = await getUserRole();
      const isTutor = userRole === 'tutor';

      // # Create mentor doc if it doesn't exist for tutors
      const mentorRef = doc(db, 'mentors', user.uid);
      const mentorDoc = await getDoc(mentorRef);

      if (!mentorDoc.exists() && isTutor) {
        // # Create initial mentor document for tutor
        await setDoc(mentorRef, {
          personal: {
            zoom_url: zoomId,
            mentor_email: email
          },
          metadata: {
            created_at: serverTimestamp(),
            last_edited_at: serverTimestamp()
          }
        });
      } else {
        // # Update existing document
        await updateDoc(mentorRef, {
          'personal.zoom_url': zoomId,
          'personal.mentor_email': email,
          'metadata.last_edited_at': serverTimestamp()
        });
      }
      
      setIsEditing(false);
      setSuccessMessage('Profile updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile');
    }
  };

  // # Calculate meeting status
  const getMeetingStatus = (meetTime) => {
    const now = new Date();
    const meetingTime = new Date(meetTime);
    const hoursUntilMeeting = (meetingTime - now) / (1000 * 60 * 60);
    const hoursSinceMeeting = (now - meetingTime) / (1000 * 60 * 60);
    
    if (hoursSinceMeeting > 6) {
      // # More than 6 hours since meeting started
      return 'past';
    } else if (hoursUntilMeeting < 0) {
      // # Meeting started but within 6-hour join window
      return 'joinable';
    } else if (hoursUntilMeeting <= 1) {
      // # Meeting starts in less than 1 hour
      return 'joinable';
    } else if (hoursUntilMeeting <= 12) {
      // # Meeting starts in less than 12 hours
      return 'upcoming-soon';
    } else {
      return 'scheduled';
    }
  };

  // # Handle create meeting button click
  const handleCreateMeeting = () => {
    // # Check if we have session storage data from mentees list
    const menteeId = sessionStorage.getItem('scheduleMeetingMenteeId');
    const menteeEmail = sessionStorage.getItem('scheduleMeetingMenteeEmail');
    const menteeStep = sessionStorage.getItem('scheduleMeetingStep');
    
    // # If we have data from mentees list, use it directly
    if (menteeId && menteeEmail && menteeStep) {
      // # Determine which meeting type to create based on step
      let meetingType = 'step1';
      if (menteeStep === 'Step 2') {
        meetingType = 'step2';
      } else if (menteeStep === 'OET') {
        meetingType = 'oet';
      } else if (menteeStep === 'US Visa') {
        meetingType = 'visa';
      }
      
      // # Set the selected meeting type
      setSelectedMeetingType(meetingType);
      
      // # Clear the session storage to avoid issues with future meetings
      sessionStorage.removeItem('scheduleMeetingMenteeId');
      sessionStorage.removeItem('scheduleMeetingMenteeEmail');
      sessionStorage.removeItem('scheduleMeetingStep');
      
      // # Show the create modal directly
      setShowCreateModal(true);
    } else {
      // # Show the type selector if we don't have data from mentees list
      setShowTypeSelector(true);
    }
  };

  // # Handle meeting type selection
  const handleSelectMeetingType = (type) => {
    setSelectedMeetingType(type);
    setShowTypeSelector(false);
    setShowCreateModal(true);
  };

  // # Handle meeting creation success
  const handleMeetingCreated = () => {
    setShowCreateModal(false);
    setSuccessMessage('Meeting created successfully!');
    setTimeout(() => setSuccessMessage(''), 3000);
    
    // Use the refreshMeetings function to ensure consistency
    refreshMeetings();
  };

  // # Handle edit meeting button click
  const handleEditMeeting = (meeting) => {
    setSelectedMeeting(meeting);
    setShowEditDialog(true);
  };

  // # Handle delete meeting button click
  const handleDeleteMeeting = (meeting) => {
    setSelectedMeeting(meeting);
    setShowDeleteDialog(true);
  };

  // # Handle meeting update success
  const handleMeetingUpdateSuccess = () => {
    setSuccessMessage('Meeting updated successfully!');
    setTimeout(() => setSuccessMessage(''), 3000);
    refreshMeetings();
  };

  // # Handle meeting deletion success
  const handleMeetingDeleteSuccess = () => {
    setSuccessMessage('Meeting deleted successfully!');
    setTimeout(() => setSuccessMessage(''), 3000);
    refreshMeetings();
  };

  // # Refresh meetings data
  const refreshMeetings = async () => {
    setLoading(true);
    try {
      // # Fetch all meeting types
      const meetingTypes = ['gen_meetings', 'step1_meetings', 'step2_meetings', 'oet_meetings', 'visa_meetings'];
      let allMeetings = [];

      for (const type of meetingTypes) {
        const meetingsRef = doc(db, 'meetings', type);
        const meetingsDoc = await getDoc(meetingsRef);

        if (meetingsDoc.exists()) {
          const data = meetingsDoc.data();
          const typeMeetings = Object.entries(data).map(([id, meeting]) => {
            // # Skip null entries (deleted meetings)
            if (meeting === null) return null;
            
            return {
              id,
              type,
              ...meeting,
              meet_time: meeting.meet_time?.toDate?.() || new Date(meeting.meet_time)
            };
          })
          .filter(meeting => 
            meeting !== null && 
            (meeting.host.uid === user.uid || 
             meeting.invited?.includes(user.uid))
          );
          
          allMeetings = [...allMeetings, ...typeMeetings];
        }
      }

      allMeetings.sort((a, b) => a.meet_time - b.meet_time);
      setMeetings(allMeetings);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to refresh meetings data');
    } finally {
      setLoading(false);
    }
  };

  // # Render meeting card with appropriate styling
  const renderMeetingCard = (meeting) => {
    const { date, time } = formatDateTime(meeting.meet_time);
    const isHost = meeting.host.uid === user.uid;
    const meetingStatus = getMeetingStatus(meeting.meet_time);
    const now = new Date();
    const meetingTime = new Date(meeting.meet_time);
    const hoursSinceMeeting = (now - meetingTime) / (1000 * 60 * 60);
    const minutesSinceMeeting = Math.floor(hoursSinceMeeting * 60);
    const hoursUntilMeeting = (meetingTime - now) / (1000 * 60 * 60);
    
    let joinButtonText = "Join Meeting";
    if (hoursSinceMeeting > 0 && hoursSinceMeeting <= 6) {
      joinButtonText = "Join Now";
    } else if (meetingStatus === 'past') {
      joinButtonText = "Meeting Ended";
    } else if (meetingStatus === 'upcoming-soon') {
      joinButtonText = "Join Soon";
    }
    
    return (
      <div key={meeting.id} className={`mntr_mtg_meeting-card ${meetingStatus}`}>
        <div className="mntr_mtg_meeting-type">
          {meeting.type.replace('_meetings', '').toUpperCase()}
        </div>
        <h4>{meeting.title}</h4>
        <p className="mntr_mtg_meeting-description">{meeting.description}</p>
        <div className="mntr_mtg_meeting-info">
          <div className="mntr_mtg_meeting-datetime">
            <span className="mntr_mtg_date">
              <FaCalendarAlt className="mntr_mtg_icon" /> {date}
            </span>
            <span className="mntr_mtg_time">
              <FaClock className="mntr_mtg_icon" /> {time}
              {hoursSinceMeeting > 0 && hoursSinceMeeting <= 6 && (
                <span className="mntr_mtg_time-ago">
                  {hoursSinceMeeting < 1 
                    ? ` (Started ${minutesSinceMeeting}m ago)`
                    : ` (Started ${Math.floor(hoursSinceMeeting)}h ago)`
                  }
                </span>
              )}
            </span>
          </div>
          <div className="mntr_mtg_meeting-role">
            {isHost ? (
              <span className="mntr_mtg_host-badge">Host</span>
            ) : (
              <span className="mntr_mtg_participant-badge">Participant</span>
            )}
          </div>
        </div>
        <div className="mntr_mtg_meeting-actions-container">
          <a 
            href={meeting.host.url}
            target="_blank"
            rel="noopener noreferrer"
            className={`mntr_mtg_join-button ${meetingStatus === 'joinable' ? 'active' : ''} ${meetingStatus === 'upcoming-soon' ? 'upcoming' : ''} ${meetingStatus === 'past' ? 'disabled' : ''}`}
            onClick={(e) => meetingStatus !== 'joinable' && e.preventDefault()}
          >
            <FaExternalLinkAlt className="mntr_mtg_icon" />
            {joinButtonText}
          </a>
          
          {/* Show edit/delete buttons only for host */}
          {isHost && (
            <MeetingActions 
              meeting={meeting} 
              onEdit={handleEditMeeting} 
              onDelete={handleDeleteMeeting} 
            />
          )}
        </div>
      </div>
    );
  };

  // # Render section of meetings
  const renderMeetingSection = (title, meetings, iconClass) => {
    if (meetings.length === 0) return null;
    
    return (
      <div className="mntr_mtg_meetings-section">
        <div className="mntr_mtg_section-header">
          <h3>
            <span className={`mntr_mtg_section-icon ${iconClass}`}></span>
            {title}
            <span className="mntr_mtg_count">{meetings.length}</span>
          </h3>
        </div>
        <div className="mntr_mtg_meetings-grid">
          {meetings.map(renderMeetingCard)}
        </div>
      </div>
    );
  };

  const groupedMeetings = groupMeetings(meetings);

  if (loading) return <div className="mntr_mtg_loading-state">Loading...</div>;
  if (error) return <div className="mntr_mtg_error-state">{error}</div>;

  return (
    <div className={`mntr_mtg_mentor-container ${isDarkMode ? 'mntr_mtg_dark-mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      
      <div className="mntr_mtg_mentor-main-content">
        <div className="mntr_mtg_title-container">
          <div className="mntr_mtg_title-box">
            <h2>Meetings Dashboard</h2>
            <div className="mntr_mtg_title-underline"></div>
          </div>
        </div>
        
        {/* # Profile Section */}
        <div className="mntr_mtg_profile-section">
          <div className="mntr_mtg_profile-header">
            <h3>Meeting Profile</h3>
            {successMessage && (
              <div className="mntr_mtg_success-message">
                <span>✓</span> {successMessage}
              </div>
            )}
          </div>
          <div className="mntr_mtg_profile-form">
            <div className="mntr_mtg_input-group">
              <label>Professional Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={!isEditing}
                placeholder="Enter your professional email"
              />
            </div>
            <div className="mntr_mtg_input-group">
              <label>Meeting URL</label>
              <input
                type="text"
                value={zoomId}
                onChange={(e) => setZoomId(e.target.value)}
                disabled={!isEditing}
                placeholder="Enter your meeting URL"
              />
            </div>
            <div className="mntr_mtg_profile-actions">
              {isEditing ? (
                <>
                  <button 
                    className="mntr_mtg_save-button"
                    onClick={handleProfileUpdate}
                  >
                    Save Changes
                  </button>
                  <button 
                    className="mntr_mtg_cancel-button"
                    onClick={() => setIsEditing(false)}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button 
                  className="mntr_mtg_edit-button"
                  onClick={() => setIsEditing(true)}
                >
                  Edit Profile
                </button>
              )}
            </div>
          </div>
        </div>

        {/* # Create Meeting Button */}
        <div className="mntr_mtg_create-meeting-container">
          <button 
            className="mntr_mtg_create-meeting-btn"
            onClick={handleCreateMeeting}
          >
            <FaPlus className="mntr_mtg_icon" /> Create New Meeting
          </button>
        </div>

        {/* # Meeting Type Selector */}
        {showTypeSelector && (
          <div className="mntr_mtg_type-selector">
            <div className="mntr_mtg_type-selector-overlay" onClick={() => setShowTypeSelector(false)}></div>
            <div className="mntr_mtg_type-selector-content">
              <h3>Select Meeting Type</h3>
              <div className="mntr_mtg_type-options">
                <button 
                  className="mntr_mtg_type-option-btn step1"
                  onClick={() => handleSelectMeetingType('step1')}
                >
                  <span className="mntr_mtg_type-icon">S1</span>
                  <span className="mntr_mtg_type-label">Step 1 Mentorship</span>
                </button>
                <button 
                  className="mntr_mtg_type-option-btn step2"
                  onClick={() => handleSelectMeetingType('step2')}
                >
                  <span className="mntr_mtg_type-icon">S2</span>
                  <span className="mntr_mtg_type-label">Step 2 Mentorship</span>
                </button>
                <button 
                  className="mntr_mtg_type-option-btn cancel"
                  onClick={() => setShowTypeSelector(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* # Meetings List */}
        <div className="mntr_mtg_meetings-container">
          {meetings.length === 0 ? (
            <p className="mntr_mtg_no-meetings">No meetings found</p>
          ) : (
            <>
              {renderMeetingSection(
                "Meetings Today", 
                groupedMeetings.within24Hours,
                "upcoming"
              )}
              
              {renderMeetingSection(
                "Meetings in 7 Days", 
                groupedMeetings.within7Days,
                "week"
              )}
              
              {renderMeetingSection(
                "Other Upcoming Meetings", 
                groupedMeetings.otherMeetings,
                "other"
              )}
              
              {renderMeetingSection(
                "Passed Meetings", 
                groupedMeetings.passedMeetings,
                "past"
              )}
            </>
          )}
        </div>
      </div>

      <DashboardFooter />

      {/* # Create Meeting Modal */}
      {showCreateModal && selectedMeetingType && (
        <MentorMeetingModal
          open={showCreateModal}
          onClose={() => {
            setShowCreateModal(false);
            setSelectedMeetingType('');
          }}
          meetingType={selectedMeetingType}
          onSuccess={handleMeetingCreated}
          mentorUrl={zoomId}
          mentorEmail={email}
          isDarkMode={isDarkMode}
        />
      )}

      {/* # Edit Meeting Dialog if needed */}
      {showEditDialog && selectedMeeting && (
        <EditMeetingDialog
          open={showEditDialog}
          onClose={() => setShowEditDialog(false)}
          meeting={selectedMeeting}
          onSuccess={handleMeetingUpdateSuccess}
        />
      )}
      
      {/* # Delete Meeting Dialog if needed */}
      {showDeleteDialog && selectedMeeting && (
        <DeleteMeetingDialog
          open={showDeleteDialog}
          onClose={() => setShowDeleteDialog(false)}
          meeting={selectedMeeting}
          onSuccess={handleMeetingDeleteSuccess}
        />
      )}
    </div>
  );
};

export default MentorMeetings; 